import React from 'react';

const formatDate = (value) => {
  if(!value) return '—';

  return new Intl.DateTimeFormat('en', {
    month:'short', day:'numeric', year:'numeric', hour:'numeric', minute:'2-digit'
  }).format(new Date(value));
};

export const formatMetric = (metric) => metric?.available ? Number(metric.value || 0).toLocaleString() : '—';

const Metric = ({label, metric, rate}) => (
  <span className={`campaign-manager-scheduled-metric ${metric?.available ? '' : 'is-unavailable'}`} title={metric?.available ? label : `${label} is not available for this source`}>
    <small>{label}</small>
    <strong>{formatMetric(metric)}</strong>
    {rate?.available ? <em>{rate.value}%</em> : null}
  </span>
);

const ScheduledCommunications = ({
  filters,
  inventory,
  loading,
  onFilterChange,
  onSearch,
  searchInput,
  setSearchInput
}) => {
  const summary = inventory?.summary;
  const items = inventory?.items || [];

  return (
    <>
      <section className="campaign-manager-metrics" aria-label="Scheduled communication summary">
        <div><span>Communications</span><strong>{summary?.total || 0}</strong><small>Across filtered HSC sources</small></div>
        <div><span>Upcoming</span><strong>{summary?.scheduled || 0}</strong><small>With a future scheduled time</small></div>
        <div><span>Attempted</span><strong>{summary?.attempted?.value || 0}</strong><small>{summary?.attempted?.available_items || 0} records report this metric</small></div>
        <div><span>Delivered</span><strong>{summary?.delivered?.value || 0}</strong><small>{summary?.delivered?.available_items || 0} records report this metric</small></div>
      </section>

      <section className="campaign-manager-queue campaign-manager-scheduled">
        <div className="campaign-manager-queue__header campaign-manager-scheduled__header">
          <div>
            <p className="campaign-manager-eyebrow">Normalized communication history</p>
            <h2>Schedules and manual batches</h2>
            <span>{summary?.total || 0} records in this view</span>
          </div>
          <form className="campaign-manager-scheduled-filters" onSubmit={onSearch}>
            <label>
              <span>Channel</span>
              <select aria-label="Scheduled channel" onChange={(event) => onFilterChange('channel', event.target.value)} value={filters.channel}>
                <option value="">All channels</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="push">Push</option>
                <option value="multichannel">Preference based</option>
              </select>
            </label>
            <label>
              <span>Source</span>
              <select aria-label="Scheduled source" onChange={(event) => onFilterChange('source', event.target.value)} value={filters.source}>
                <option value="">All sources</option>
                <option value="campaign_manager">Campaign Manager</option>
                <option value="hsc_scheduler">HSC Scheduler</option>
                <option value="hsc_sms_batch">HSC SMS batch</option>
                <option value="hsc_push">HSC Push</option>
              </select>
            </label>
            <label>
              <span>Status</span>
              <select aria-label="Scheduled status" onChange={(event) => onFilterChange('status', event.target.value)} value={filters.status}>
                <option value="">All statuses</option>
                <option value="scheduled">Scheduled</option>
                <option value="ready">Ready</option>
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="queued">Queued</option>
                <option value="in_progress">In progress</option>
                <option value="sent">Sent</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </label>
            <label className="campaign-manager-scheduled-filters__search">
              <span>Search</span>
              <div>
                <input
                  aria-label="Search scheduled communications"
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Name, audience, or provider"
                  type="search"
                  value={searchInput}
                />
                <button type="submit">Search</button>
              </div>
            </label>
          </form>
        </div>

        {loading ?
          <div className="campaign-manager-loading"><span /> Loading scheduled communications…</div>
          :
          <div className="campaign-manager-table-wrap">
            <table className="campaign-manager-table campaign-manager-scheduled-table">
              <thead>
                <tr>
                  <th>Communication</th>
                  <th>Channel / status</th>
                  <th>Audience</th>
                  <th>Schedule</th>
                  <th>Provider progress</th>
                  <th>Engagement</th>
                  <th>Activity</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.name}</strong>
                      <span>{item.subject || item.source_label}</span>
                      <small className="campaign-manager-source-label">{item.source_label}</small>
                    </td>
                    <td>
                      <span className="campaign-manager-channel-label">{item.channel === 'multichannel' ? 'Preference based' : item.channel}</span>
                      <span className={`campaign-manager-status-label campaign-manager-status--${item.status}`}>{item.status.replace('_', ' ')}</span>
                    </td>
                    <td><strong>{item.audience_label}</strong><span>{item.provider}</span></td>
                    <td>{item.scheduled_for ? <><strong>{formatDate(item.scheduled_for)}</strong><span>Scheduled time</span></> : <span className="campaign-manager-muted">Manual / event driven</span>}</td>
                    <td>
                      <div className="campaign-manager-scheduled-metrics">
                        <Metric label="Targeted" metric={item.metrics.targeted} />
                        <Metric label="Attempted" metric={item.metrics.attempted} />
                        <Metric label="Accepted" metric={item.metrics.accepted} />
                        <Metric label="Delivered" metric={item.metrics.delivered} rate={item.rates.delivery} />
                        <Metric label="Failed" metric={item.metrics.failed} />
                        <Metric label="Suppressed" metric={item.metrics.suppressed} />
                      </div>
                    </td>
                    <td>
                      <div className="campaign-manager-scheduled-metrics campaign-manager-scheduled-metrics--engagement">
                        <Metric label="Opened" metric={item.metrics.opened} rate={item.rates.open} />
                        <Metric label="Clicked" metric={item.metrics.clicked} rate={item.rates.click} />
                        <Metric label="Bounced" metric={item.metrics.bounced} />
                        <Metric label="Complaints" metric={item.metrics.complained} />
                        <Metric label="Unsubscribed" metric={item.metrics.unsubscribed} />
                      </div>
                    </td>
                    <td><strong>{formatDate(item.last_activity_at)}</strong><span>{item.metric_note}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length === 0 ? <div className="campaign-manager-empty">No communications match this view.</div> : null}
          </div>
        }
      </section>

      <p className="campaign-manager-metric-note">A dash means the source does not retain that metric. Open and click counts are unique tracked contacts and can be affected by email privacy controls.</p>
    </>
  );
};

export default ScheduledCommunications;
